import { Typography, Stack, Box } from '@mui/material'
import { Autocomplete } from 'components/Autocomplete/Autocomplete'
import Input from 'components/Input/Input'
import { InputWithDropdown } from 'components/InputWithDropdown/InputWithDropdown'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { FormikErrors, useFormikContext } from 'formik'
import { useCodeList } from 'hooks/useCodeList'
import { t } from 'i18next'
import theme from 'lib/theme'
import { ProductNutrients } from './ProductNutrients'
import { NutrientHeaderType } from './ProductNutritionalInfo'

export const NutrientHeader = ({
  index,
  header,
}: {
  index: number
  header: NutrientHeaderType
}) => {
  const {
    codeListTypes: measurementUnitCodes,
    error,
    isLoading,
  } = useCodeList('measurementUnitCode', true)
  const { codeListTypes: preparationStateCodes } = useCodeList(
    'preparationStateCode',
    true
  )
  const { errors, setFieldValue } = useFormikContext()
  const formatFieldName = (name: string) => {
    return `nutrientHeaders.${index}.${name}`
  }
  const formatErrorMessage = (name: string) => {
    return errors['nutrientHeaders' as keyof FormikErrors<unknown>]?.[index]?.[
      name
    ]
  }

  if (isLoading) return <LoadingIndicator />
  if (error) return <h1>Error</h1>
  return (
    <>
      <Typography variant='body2' mt={6}>
        {t('nutrientGroup')}
      </Typography>
      <Box mt={5} mb={0} display='flex'>
        <Input
          width='48%'
          errorMessage={
            formatErrorMessage('nutrientBasisQuantity') as string | undefined
          }
          label={t('nutrientBasisQuantity')}
          name={formatFieldName('nutrientBasisQuantity')}
          type='text'
          variant='filled'
        />
        <Autocomplete
          options={measurementUnitCodes}
          onChange={(newUnit) =>
            setFieldValue(formatFieldName('nutrientBasisQuantityCode'), newUnit)
          }
          onClear={() => setFieldValue('nutrientBasisQuantityCode', null)}
          value={header?.nutrientBasisQuantityCode}
          name={`nutrientHeaders.${index}.nutrientBasisQuantityCode`}
          label='Nutrient unit'
          ml={theme.spacing(2)}
        />
      </Box>
      <Box mb={4} display='flex'>
        {' '}
        <Autocomplete
          options={preparationStateCodes}
          onChange={(newUnit) =>
            setFieldValue(formatFieldName('preparationStateCode'), newUnit)
          }
          onClear={() => setFieldValue('preparationStateCode', null)}
          value={header?.preparationStateCode}
          label={t('preparationState')}
          name={`nutrientHeaders.${index}.preparationStateCode`}
        />
      </Box>
      <Stack spacing={3}>
        <InputWithDropdown
          label={t('desc')}
          value={
            header?.servingSizeDescription.options[
              header?.servingSizeDescription.contentLanguage
            ]
          }
          selectedOption={header?.servingSizeDescription.contentLanguage}
          dropdownFieldName={formatFieldName(
            'servingSizeDescription.contentLanguage'
          )}
          inputFieldName={formatFieldName('servingSizeDescription.options')}
          inputOptions={header?.servingSizeDescription.options}
        />
        <ProductNutrients
          measurementUnitCodes={measurementUnitCodes}
          mandatoryNutrientDetails={header?.mandatoryNutrientDetails}
          nutrientDetails={header?.nutrientDetails}
          index={index}
        />
        <InputWithDropdown
          multiline
          label={t('dailyIntakeReference')}
          value={
            header?.dailyValueIntakeReference.options[
              header?.dailyValueIntakeReference.contentLanguage
            ]
          }
          selectedOption={header?.dailyValueIntakeReference.contentLanguage}
          dropdownFieldName={formatFieldName(
            'dailyValueIntakeReference.contentLanguage'
          )}
          inputFieldName={formatFieldName('dailyValueIntakeReference.options')}
          inputOptions={header?.dailyValueIntakeReference.options}
        />
      </Stack>
    </>
  )
}
